import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Paper, Title, Text, Stack, Button, Group } from '@mantine/core';
import { IconMail, IconUser } from '@tabler/icons-react';
import Layout from '../../components/Layout/Layout';
import { CONTACT_EMAIL, getActiveSeason } from '../../config/seasons';

/**
 * RegistrationClosed
 *
 * Rendered by IndividualRegistration and SchoolRegistration when the season is
 * not open. `variant` tailors the message to the user's current state.
 *
 * Variants:
 *   'no_reg'         - User has no registration for this closed season.
 *   'draft'          - User has an incomplete draft; data is preserved.
 *   'paid_no_music'  - User paid but hasn't submitted music; needs manual help.
 *   'school_no_acct' - Teacher/school without a school_account for closed season.
 */
const RegistrationClosed = ({ variant = 'no_reg', year = getActiveSeason().year }) => {
  const config = COPY[variant] || COPY.no_reg;

  return (
    <Layout title="Registration Closed - Prix du Canada">
      <Container size="sm" style={{ padding: '4rem 1rem' }}>
        <Paper
          radius="md"
          p="xl"
          withBorder
          style={{ borderTop: '4px solid var(--primary-gold)' }}
        >
          <Stack spacing="lg">
            <Title order={2} style={{ color: 'var(--charcoal)', textAlign: 'center' }}>
              {config.title(year)}
            </Title>

            <Text size="md" style={{ color: 'var(--charcoal)', lineHeight: 1.7 }}>
              {config.body(year)}
            </Text>

            {config.actionRequired && (
              <Paper
                p="md"
                radius="sm"
                style={{ background: 'var(--soft-cream)', border: '1px solid var(--primary-gold)' }}
              >
                <Text size="sm" weight={600} mb="xs" style={{ color: 'var(--deep-gold)' }}>
                  Action required
                </Text>
                <Text size="sm" style={{ color: 'var(--charcoal)' }}>
                  {config.actionRequired}
                </Text>
              </Paper>
            )}

            <Text size="sm" color="dimmed" style={{ textAlign: 'center' }}>
              Questions? Reach us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
                {CONTACT_EMAIL}
              </a>
            </Text>

            <Group justify="center" mt="md">
              <Button
                component={Link}
                to="/profile"
                leftSection={<IconUser size={16} />}
                variant="light"
              >
                View my profile
              </Button>
              <Button
                component="a"
                href={`mailto:${CONTACT_EMAIL}`}
                leftSection={<IconMail size={16} />}
                style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
              >
                Contact us
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Container>
    </Layout>
  );
};

const COPY = {
  no_reg: {
    title: (year) => `Registration for ${year} is closed`,
    body: (year) =>
      `Thank you for your interest in Prix du Canada ${year}. Registration has now closed and our dancers are preparing to take the stage. Come cheer them on — and stay tuned, we'd love to have you with us next season.`,
    actionRequired: null,
  },
  draft: {
    title: (year) => `Registration for ${year} is closed`,
    body: (year) =>
      `Registration for Prix du Canada ${year} has closed. We'd love to have you next year — in the meantime, come cheer this year's dancers on! We'll be in touch when the next season opens.`,
    actionRequired: null,
  },
  paid_no_music: {
    title: () => `Your music is still needed`,
    body: () =>
      `Your registration is paid and confirmed, but we don't have your music file yet. Registration is closed on our end, so please reach out and we'll help you finalize your submission manually.`,
    actionRequired: `Email us your music file (MP3/WAV, under 3 minutes) and we'll attach it to your registration.`,
  },
  school_no_acct: {
    title: (year) => `School registration for ${year} is closed`,
    body: (year) =>
      `Registration for Prix du Canada ${year} has closed. If your school would like to participate in an upcoming season, please get in touch and we'll let you know as soon as registration reopens.`,
    actionRequired: null,
  },
};

export default RegistrationClosed;
